/**
 * Polling hook for the Missions surface (Mode D, Part D of the
 * Team-of-Agents handoff).
 *
 * Drives `useMissionsStore` by polling `GET /api/mission/missions` (and a
 * `getMission` drill-in on cancel) on a dual cadence:
 *   - **5s** when any mission is in an ACTIVE state
 *     (queued|submitted|planning|running|awaiting_user|synthesizing).
 *   - **30s** when the snapshot is otherwise idle.
 *
 * Page-Visibility gating: when the tab is hidden for more than
 * `VIS_PAUSE_MS` (60s), polling backs off to the SLOW cadence
 * regardless of activity — `awaiting_user` missions can sit in the
 * 7-day human-gate window, hammering the API every 5s buys nothing.
 * On `visibilitychange → "visible"` we fire an immediate refetch so the
 * pane reflects reality the moment the lawyer returns.
 *
 * Dedup: a poller-instance `inFlight` map keyed by `caseId` collapses
 * overlapping fetches (an explicit `refetch()` during a scheduled tick
 * reuses the in-flight promise instead of opening a second connection).
 *
 * Cleanup safety: a per-instance `cancelled` flag guarantees that a
 * late-resolving fetch (e.g. lexserver took 4s, hook unmounted at 1s)
 * NEVER mutates the store after disposal. The per-call AbortController
 * lives in the client (8s timeout); the poller itself uses the flag-
 * based pattern that cousin polling hooks (`useTaskFrames`,
 * `useLlmStatus`) already follow.
 *
 * Error surface: poll failures go to `store.pollError` (string) and do
 * NOT throw. The pane reads `pollError` as a fail-soft chip; the next
 * successful poll clears it via `hydrateFromList`.
 *
 * Cancel-of-queued race: mission-service's DELETE is followed by a
 * `getMission` confirmation fetch. A 404 on that drill-in is treated
 * as a successful terminal-by-deletion (→ `removeMission`), NOT an
 * error. NOTE: this branch is defensive against a future contract
 * change — today's `mission-coordinator.cancel()` sets
 * `state: "canceled"` and persists the record (it does NOT purge), so
 * the 404 path is not exercised in production yet.
 *
 * Testability: the orchestration logic lives in `createMissionPoller`
 * (pure factory) so unit tests can drive it with stub deps + fake
 * timers without `renderHook`. The React hook is a thin `useEffect`
 * wrapper. Per lexide convention (`useFeedback.test.ts`): non-render
 * logic gets vitest coverage; hook-render semantics are reserved for
 * Playwright.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLexEventStream } from "@/hooks/useEventStream";
import { useShallow } from "zustand/react/shallow";

import {
  cancelMission as apiCancelMissionDefault,
  getMission as getMissionDefault,
  listMissions as listMissionsDefault,
  MissionApiError,
  type ListMissionsFilters,
  type MissionRecord,
  type MissionState,
  type MissionSummary,
} from "@/api/lx-mission";
import {
  selectHasActiveMissions,
  selectMissionsByCase,
  useMissionsStore,
  type MissionProgressEvent,
  type MissionSlot,
  type MissionsStoreState,
} from "@/stores/useMissionsStore";

export const POLL_FAST_MS = 5_000;
export const POLL_SLOW_MS = 30_000;
/** When the tab has been hidden for at least this long we slow polling
 *  down to `POLL_SLOW_MS` (we never STOP polling — that would let the
 *  awaiting-user banner go stale and break the load-bearing return-to-
 *  app UX). Choose a value larger than `POLL_FAST_MS` so a briefly-hidden
 *  tab doesn't lose its fast cadence. */
export const VIS_PAUSE_MS = 60_000;
/** Max parallel `getMission` drill-ins per tick. Small on purpose: these
 *  ride the same lexserver→plugin→mission-service proxy chain as the list
 *  poll, and a lawyer realistically has a handful of live runs. */
export const DRILL_IN_CONCURRENCY = 4;

/** Terminal states never change again, so their records are cached
 *  permanently after the first drill-in. */
function isTerminalState(state: MissionState): boolean {
  return state === "completed" || state === "failed" || state === "canceled";
}

// ─── Pure factory (the unit-tested surface) ───────────────────────────

export interface MissionPollerDeps {
  /** API surface — defaulted to the real `lx-mission` client. Tests
   *  inject mocks. */
  listMissions?: typeof listMissionsDefault;
  getMission?: typeof getMissionDefault;
  cancelMissionApi?: typeof apiCancelMissionDefault;
  /** Store actions + reads — defaulted to the real `useMissionsStore`.
   *  Tests inject a stub so behaviour can be asserted without sharing
   *  the module-level singleton. */
  store?: {
    hydrateFromList: MissionsStoreState["hydrateFromList"];
    upsertMission: MissionsStoreState["upsertMission"];
    markCanceling: MissionsStoreState["markCanceling"];
    removeMission: MissionsStoreState["removeMission"];
    setPollError: MissionsStoreState["setPollError"];
    setLastPolledAt: MissionsStoreState["setLastPolledAt"];
    hasActiveMissions: () => boolean;
    /** Read a slot to decide whether its full record still needs
     *  fetching (see `needsRecordFetch`). */
    getSlot: (missionId: string) => MissionSlot | undefined;
    /** Merge a `mission.progress` push payload as a live overlay. */
    applyMissionProgress: MissionsStoreState["applyMissionProgress"];
  };
  /** Visibility surface — defaulted to `document.visibilityState` +
   *  `visibilitychange`. Tests inject a controllable stub. */
  visibility?: {
    isVisible: () => boolean;
    addListener: (handler: () => void) => () => void;
  };
  /** Time + timer hooks — defaulted to wall-clock + global timers.
   *  Tests inject deterministic stubs. */
  now?: () => number;
  setTimer?: (cb: () => void, ms: number) => unknown;
  clearTimer?: (token: unknown) => void;
}

export interface MissionPoller {
  /** Start the polling loop — fires an immediate fetch, then schedules
   *  recurring ticks. Idempotent (calling twice is a no-op). */
  start: () => void;
  /** Stop the loop + invalidate any in-flight result. Idempotent. */
  dispose: () => void;
  /** Force a fetch right now (deduped with any in-flight). */
  refetch: () => Promise<void>;
  /** Cancel a mission (optimistic mark → DELETE → drill-in). 404 on
   *  drill-in → removeMission (queued-cancel race). */
  cancelMission: (missionId: string) => Promise<void>;
  /** Notify the poller that the page just became visible — fires an
   *  immediate fetch + resets the hidden timer. Bound via the
   *  visibility listener inside `start()`, exposed for tests. */
  notifyVisibilityChange: () => void;
  /** Inspect the current scheduled tick for assertion (test-only). */
  __getState: () => {
    cancelled: boolean;
    timerToken: unknown;
    hiddenSinceMs: number | null;
    started: boolean;
  };
}

export interface CreateMissionPollerOptions extends MissionPollerDeps {
  caseId: string | null;
}

function defaultVisibility(): NonNullable<MissionPollerDeps["visibility"]> {
  if (typeof document === "undefined") {
    return { isVisible: () => true, addListener: () => () => undefined };
  }
  return {
    isVisible: () => document.visibilityState !== "hidden",
    addListener: (handler) => {
      document.addEventListener("visibilitychange", handler);
      return () => document.removeEventListener("visibilitychange", handler);
    },
  };
}

function defaultStore(): NonNullable<MissionPollerDeps["store"]> {
  return {
    hydrateFromList: (summaries, opts) =>
      useMissionsStore.getState().hydrateFromList(summaries, opts),
    upsertMission: (record) =>
      useMissionsStore.getState().upsertMission(record),
    markCanceling: (id) => useMissionsStore.getState().markCanceling(id),
    removeMission: (id) => useMissionsStore.getState().removeMission(id),
    setPollError: (msg) => useMissionsStore.getState().setPollError(msg),
    setLastPolledAt: (ts) => useMissionsStore.getState().setLastPolledAt(ts),
    hasActiveMissions: () => selectHasActiveMissions(useMissionsStore.getState()),
    getSlot: (missionId) => useMissionsStore.getState().missions.get(missionId),
    applyMissionProgress: (payload) =>
      useMissionsStore.getState().applyMissionProgress(payload),
  };
}

export function createMissionPoller(
  opts: CreateMissionPollerOptions,
): MissionPoller {
  const listMissionsImpl = opts.listMissions ?? listMissionsDefault;
  const getMissionImpl = opts.getMission ?? getMissionDefault;
  const cancelImpl = opts.cancelMissionApi ?? apiCancelMissionDefault;
  const store = opts.store ?? defaultStore();
  const vis = opts.visibility ?? defaultVisibility();
  const now = opts.now ?? (() => Date.now());
  const setTimer = opts.setTimer ?? ((cb, ms) => setTimeout(cb, ms));
  const clearTimer = opts.clearTimer ?? ((t) => clearTimeout(t as ReturnType<typeof setTimeout>));
  const caseId = opts.caseId;

  let cancelled = false;
  let timerToken: unknown = null;
  let hiddenSinceMs: number | null = null;
  let started = false;
  let removeVisListener: (() => void) | null = null;
  let inFlight: Promise<void> | null = null;
  /** Missions whose drill-in failed PERMANENTLY (403/404). Without this,
   *  a mission the list returns but the caller may not read would be
   *  re-fetched on every single tick, forever — an endless stream of
   *  forbidden requests. Observed live: `GET /missions/:id` → 403 twice
   *  per tick. Transient failures are deliberately NOT recorded here, so
   *  a blip still recovers on the next tick. */
  const permanentlyUnreadable = new Set<string>();

  const filter: ListMissionsFilters = caseId ? { caseId } : {};

  /** True when this mission's full record must be (re)fetched.
   *
   *  - No record yet            → always fetch (the pane has no steps at all).
   *  - Non-terminal state       → refetch every tick (node status + the live
   *                               `progress` frame change while it runs).
   *  - Terminal AND already recorded → NEVER refetch. A completed/failed/
   *    canceled record is immutable, so caching it keeps the drill-in cost
   *    proportional to ACTIVE missions rather than to history length. */
  const needsRecordFetch = (summary: MissionSummary): boolean => {
    if (permanentlyUnreadable.has(summary.missionId)) return false;
    const slot = store.getSlot(summary.missionId);
    if (!slot?.record) return true;
    return !isTerminalState(summary.state);
  };

  /** Fetch full records for the missions that need one, at bounded
   *  concurrency. Failures are swallowed per-mission: a drill-in is an
   *  ENRICHMENT, so one bad record must not blank the pane or raise the
   *  poll error chip — the summary row still renders without its steps. */
  const drillInMissions = async (
    summaries: MissionSummary[],
  ): Promise<void> => {
    const pending = summaries.filter(needsRecordFetch);
    if (pending.length === 0) return;
    let cursor = 0;
    const worker = async (): Promise<void> => {
      while (!cancelled) {
        const index = cursor++;
        if (index >= pending.length) return;
        const summary = pending[index];
        if (summary === undefined) return;
        try {
          const record = await getMissionImpl(summary.missionId);
          if (cancelled) return;
          store.upsertMission(record);
        } catch (err) {
          // Enrichment-only: keep the summary row, skip its steps. A
          // 403/404 will never succeed on retry, so stop asking.
          if (
            err instanceof MissionApiError &&
            (err.status === 403 || err.status === 404)
          ) {
            permanentlyUnreadable.add(summary.missionId);
          }
        }
      }
    };
    const lanes = Math.min(DRILL_IN_CONCURRENCY, pending.length);
    await Promise.all(Array.from({ length: lanes }, () => worker()));
  };

  const fetchOnce = async (): Promise<void> => {
    if (inFlight) return inFlight;
    const p = (async () => {
      try {
        const { missions: summaries }: { missions: MissionSummary[] } =
          await listMissionsImpl(filter);
        if (cancelled) return;
        store.hydrateFromList(summaries, { caseId: caseId ?? undefined });
        // STEP-LEVEL DRILL-IN (sidebar redesign 2026-07-19). `GET /missions`
        // returns SUMMARIES ONLY — no `plan`, no `nodes` — so before this the
        // pane could never render a run's steps and degenerated into a flat
        // list of goals (the operator's verdict). Fetch the full record for
        // every mission that needs one; `needsRecordFetch` makes this
        // self-limiting: a terminal mission is fetched exactly once and then
        // cached forever (its record is immutable), so steady-state cost is
        // one drill-in per ACTIVE mission per tick.
        await drillInMissions(summaries);
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "mission poll failed";
        store.setPollError(msg);
        // Mark that a poll ATTEMPT happened so the hook's
        // `isLoading → false` transition fires even when the first
        // tick fails. `lastPolledAt` semantics: "wall-clock ms of the
        // last poll attempt (success or failure)". Cleared back to
        // null only on `clear()` / case-switch.
        store.setLastPolledAt(now());
      }
    })();
    inFlight = p;
    try {
      await p;
    } finally {
      inFlight = null;
    }
  };

  const tick = async (): Promise<void> => {
    if (cancelled) return;

    if (!vis.isVisible()) {
      if (hiddenSinceMs === null) hiddenSinceMs = now();
      const hiddenFor = now() - hiddenSinceMs;
      if (hiddenFor > VIS_PAUSE_MS) {
        // Slow-cadence reschedule WITHOUT fetching — keep the loop alive
        // so visible-flip can wake it instantly.
        if (timerToken !== null) clearTimer(timerToken);
        timerToken = setTimer(() => void tick(), POLL_SLOW_MS);
        return;
      }
    } else {
      hiddenSinceMs = null;
    }

    await fetchOnce();
    if (cancelled) return;
    const ms = store.hasActiveMissions() ? POLL_FAST_MS : POLL_SLOW_MS;
    // Defensive clear-before-set: under visibility-flap + external
    // refetch races, a stale timer may still be live; replace it so
    // we never end up with two scheduled ticks.
    if (timerToken !== null) clearTimer(timerToken);
    timerToken = setTimer(() => void tick(), ms);
  };

  const notifyVisibilityChange = (): void => {
    if (cancelled) return;
    if (vis.isVisible()) {
      hiddenSinceMs = null;
      if (timerToken !== null) {
        clearTimer(timerToken);
        timerToken = null;
      }
      void tick();
    } else {
      // Stamp the hidden-since timestamp at the EXACT moment of the
      // visibility transition. Pre-fix this was set lazily inside
      // `tick`, so the first hidden tick always computed
      // `hiddenFor === 0` and fetched once more — contradicting the
      // docstring's "hidden > VIS_PAUSE_MS backs off".
      if (hiddenSinceMs === null) hiddenSinceMs = now();
    }
  };

  return {
    start: () => {
      if (started || cancelled) return;
      started = true;
      removeVisListener = vis.addListener(notifyVisibilityChange);
      void tick();
    },

    dispose: () => {
      cancelled = true;
      if (timerToken) {
        clearTimer(timerToken);
        timerToken = null;
      }
      if (removeVisListener) {
        removeVisListener();
        removeVisListener = null;
      }
    },

    refetch: () => fetchOnce(),

    cancelMission: async (missionId: string) => {
      store.markCanceling(missionId);
      try {
        await cancelImpl(missionId);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "cancel failed";
        store.setPollError(msg);
        throw err;
      }
      try {
        const rec: MissionRecord = await getMissionImpl(missionId);
        store.upsertMission(rec);
      } catch (err) {
        if (err instanceof MissionApiError && err.status === 404) {
          store.removeMission(missionId);
          return;
        }
        const msg =
          err instanceof Error ? err.message : "post-cancel drill-in failed";
        store.setPollError(msg);
      }
    },

    notifyVisibilityChange,

    __getState: () => ({
      cancelled,
      timerToken,
      hiddenSinceMs,
      started,
    }),
  };
}

// ─── React hook (thin `useEffect` wrapper) ────────────────────────────

export interface UseMissionsPollOptions {
  /** When provided, scope all polls + selectors to a single case. When
   *  `null` (the default for the cross-case Bell badge), the hook polls
   *  the org-wide feed (`caseId` query omitted). */
  caseId?: string | null;
}

export interface UseMissionsPollResult {
  /** Slots scoped to `caseId` (or all when `caseId === null`). */
  missions: ReturnType<typeof selectMissionsByCase>;
  /** True only before the first fetch resolves; false thereafter
   *  regardless of subsequent errors (so the pane doesn't flicker
   *  between "loading…" and a stale snapshot on transient failures). */
  isLoading: boolean;
  /** Last poll error message; `null` after a successful poll. */
  error: string | null;
  /** Wall-clock ms of the last poll attempt (success OR failure). The
   *  first failed poll also stamps this so `isLoading` clears. */
  lastPolledAt: number | null;
  /** Force an immediate fetch. */
  refetch: () => Promise<void>;
  /** Cancel a mission (see `createMissionPoller#cancelMission`). */
  cancelMission: (missionId: string) => Promise<void>;
}

export function useMissionsPoll(
  opts: UseMissionsPollOptions = {},
): UseMissionsPollResult {
  const caseId = opts.caseId ?? null;
  const missions = useMissionsStore(
    useShallow((s) => selectMissionsByCase(s, caseId ?? undefined)),
  );
  const error = useMissionsStore((s) => s.pollError);
  const lastPolledAt = useMissionsStore((s) => s.lastPolledAt);
  const [isLoading, setIsLoading] = useState<boolean>(missions.length === 0);

  const pollerRef = useRef<MissionPoller | null>(null);

  // LIVE feed (operator ruling 2026-07-18: async agent progress renders ONLY
  // here, on the activity sidebar — never in chat). The runtime pushes a
  // throttled `mission.progress` broadcast on every coordinator transition
  // ([M:mission-chat-progress] wire); each event triggers an immediate
  // refetch (deduped in-flight), so the pane tracks node progress at ~1s
  // latency instead of the slow poll cadence.
  //
  // The payload is ALREADY plan-ordered and label-resolved
  // (`nodes:[{id,label,state,pct?,message?}]`), so it is applied to the
  // store DIRECTLY as a live overlay before the refetch is issued: the
  // step rows mutate in place the instant the event lands, instead of
  // waiting a list+drill-in round-trip. The refetch still follows to pull
  // the authoritative record (timings, cost, errors, resultRef).
  useLexEventStream("mission.progress", useCallback((payload: unknown) => {
    useMissionsStore.getState().applyMissionProgress(payload as MissionProgressEvent);
    void pollerRef.current?.refetch();
  }, []));

  useEffect(() => {
    // Case-switch reset: clear any stale error chip + restart the
    // loading spinner so the new pane shows a clean "fetching…" state
    // until its first poll resolves. (`lastPolledAt` is global, so the
    // null → non-null transition only fires once unless we re-arm it
    // here.) Mutate the store BEFORE starting the poller so the
    // subscriber below observes the reset, not the wake fetch's
    // result.
    setIsLoading(true);
    useMissionsStore.getState().setPollError(null);

    const poller = createMissionPoller({ caseId });
    pollerRef.current = poller;
    poller.start();
    // Subscribe BEFORE the first poll fires. Cut loading on the next
    // `lastPolledAt` mutation (any value change, success OR failure)
    // — we set isLoading(true) above, and `setIsLoading(false)` is a
    // no-op once false, so re-firing on every later poll is harmless.
    // Using prev/current INEQUALITY (not "null → non-null") matters on
    // case-switch: `lastPolledAt` may already be non-null from the
    // previous case, so a strict transition would never clear the
    // newly-restarted spinner.
    const unsub = useMissionsStore.subscribe((state, prev) => {
      if (state.lastPolledAt !== prev.lastPolledAt) {
        setIsLoading(false);
      }
    });
    return () => {
      poller.dispose();
      pollerRef.current = null;
      unsub();
    };
  }, [caseId]);

  const refetch = useCallback(async () => {
    await pollerRef.current?.refetch();
  }, []);

  const cancelMission = useCallback(async (missionId: string) => {
    await pollerRef.current?.cancelMission(missionId);
  }, []);

  return { missions, isLoading, error, lastPolledAt, refetch, cancelMission };
}
